<section class="hero is-info is-small">
  <div class="hero-body">
    <div class="container">
      <h1 class="title">
        <span class="icon-text">
          <span class="icon"><i class="fa-solid fa-magnifying-glass-chart" aria-hidden="true"></i></span>
          <span>Enumeration &amp; Verification</span>
        </span>
      </h1>
    </div>
  </div>
</section>

<section class="section container">
  <div class="content methodology-content">
    <p class="lead">SmallCats is built by writing down every category with $n$ morphisms and $k$ objects, once each.
      The interesting word there is "once": the obvious approach produces each category hundreds or thousands of times
      over, in every possible labelling, and then has to throw the duplicates away. We don't do that any more. Here's
      how the search avoids producing a duplicate in the first place, how we check the answer afterwards, and a note
      about why two of our counts differ from those reported by Cruttwell and Leblanc.</p>

    <p><i>Disclaimer</i> This is a mostly-AI-written explanation of the algorithm.</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-list-ol" aria-hidden="true"></i></span>
        <span>Representing a category by a table</span>
      </span>
    </h2>
    <p>Fix nonnegative integers $n$ and $k$. Let $T(n,k)$ be the number of isomorphism classes of categories with $n$
      morphisms and $k$ objects. The morphisms include the $k$ identities, so necessarily $k\leq n$. We count categories
      up to isomorphism, not merely up to equivalence, and take $T(0,0)=1$ for the empty category.</p>
    <p>We label the morphisms by $M$, reserving the subset $O$ for the identities:</p>
    $$
    M=\{0,\dots,n-1\},
    \qquad
    O=\{0,\dots,k-1\}\subseteq M.
    $$
    <p>Here each object is identified with its identity morphism. For $f\in M$, write $d(f)$ and $c(f)$ for the identity
      morphisms of its domain and codomain; in particular, $d(i)=c(i)=i$ for $i\in O$. We store composition in an
      $n\times n$ table $A$, using the extra value $n$ to mean that a composite is undefined:</p>
    $$
    A_{f,g}=
    \begin{cases}
    f\circ g, &amp; d(f)=c(g),\\
    n, &amp; d(f)\neq c(g).
    \end{cases}
    $$
    <p>Our convention is that $f\circ g$ means “first $g$, then $f$.” The identity and associativity axioms become the
      constraints</p>
    $$
    A_{f,d(f)}=f=A_{c(f),f}
    $$
    <p>and</p>
    $$
    A_{A_{f,g},h}=A_{f,A_{g,h}}
    $$
    <p>whenever $d(f)=c(g)$ and $d(g)=c(h)$. Together with the domain, codomain, and composability constraints, these
      are exactly the category axioms in multiplication-table form. So a category with $n$ morphisms and $k$ objects is
      just a table satisfying finitely many constraints, and enumerating categories means enumerating those tables.</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-gears" aria-hidden="true"></i></span>
        <span>The trouble with labels</span>
      </span>
    </h2>
    <p>One category usually appears under many labellings. The $k$ identity morphisms may be permuted among themselves,
      and the other $n-k$ morphisms may be permuted among themselves, so the relevant relabelling group is</p>
    <div class="methodology-formula">
      $$
      G_{n,k}=S_k\times S_{n-k},
      $$
    </div>
    <p>where $S_r$ is the symmetric group on $r$ labels. Extend each $\sigma\in G_{n,k}$ by setting $\sigma(n)=n$, so
      that the undefined value stays put. The relabelled table is</p>
    <div class="methodology-formula">
      $$
      (\sigma\cdot A)_{f,g}
      =\sigma\!\left(A_{\sigma^{-1}(f),\sigma^{-1}(g)}\right),
      $$
    </div>
    <p>and two tables describe isomorphic categories exactly when they lie in the same orbit of this action.</p>
    <p>Now, $\lvert G_{n,k}\rvert$ is enormous: it's $4{,}320$ for $(9,3)$ and $17{,}280$ for $(10,4)$. If we just ask
      a solver for every table satisfying the axioms, we get back up to that many copies of each category, and we're
      left holding hundreds of millions of tables that we have to sort out afterwards. That is how this database used
      to be built. It works, but it's slow, it produces enormous intermediate files, and every step of the
      deduplication is a chance to get the group wrong and lose a category. So instead we arrange for the search to
      produce one table per isomorphism class and no more.</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-shapes" aria-hidden="true"></i></span>
        <span>Fixing the shape first</span>
      </span>
    </h2>
    <p>Here's the key observation. Forget composition for a moment and keep only the domains and codomains: what's left
      is a directed multigraph on the $k$ objects, with one edge for each of the $n-k$ nonidentity morphisms. Call its
      isomorphism class the <i>shape</i> of the category.</p>
    <p>Every category has exactly one shape. And if we fix a shape and consider only categories with that shape, then
      two of them are isomorphic exactly when some automorphism of the shape carries one to the other — a relabelling
      that changed the shape would have landed us somewhere else entirely. So if we enumerate the shapes first, one per
      isomorphism class, the group we still have to worry about shrinks from $G_{n,k}$ all the way down to
      $\operatorname{Aut}(\text{shape})$, which is usually trivial.</p>
    <p>Fixing the shape buys us something else too. Once the domains and codomains are pinned down, the entry $A_{f,g}$
      can only be a morphism with the domain of $g$ and the codomain of $f$ — that is, an element of a particular
      hom-set, which for these sizes usually has one or two elements in it. Every cell of the table starts out with a
      very short list of possibilities.</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-layer-group" aria-hidden="true"></i></span>
        <span>Filling in the table</span>
      </span>
    </h2>
    <p>With a shape fixed, we fill in the table by backtracking, checking associativity as we go. The order we fill the
      cells in is what makes the duplicates go away, so it's worth being precise about it: we fill in cells by
      <i>level</i>, where level $d$ means the row and column of morphism $d$. Filling in level $d$ completes the
      $(d+1)\times(d+1)$ square in the top left corner, so at every stage the part of the table we've written down is a
      square subtable.
    </p>
    <p>That's the point. Whenever we finish a level, we can take any automorphism of the shape that maps that square to
      itself, apply it, and compare the result with what we have. If it comes out smaller, we're looking at a table
      that some other branch of the search will find in its least form, and we can abandon this branch immediately. If
      it comes out larger, that automorphism can never make anything smaller later on either, so we drop it and stop
      checking it. If it ties, we keep it and check again at the next level.</p>
    <p>At the last level the square is the whole table and every automorphism maps it to itself, so every automorphism
      still in play gets compared. What survives is the least table in its $\operatorname{Aut}(\text{shape})$ orbit —
      and there is exactly one of those per orbit. No deduplication pass, no canonical form to compute, no enormous
      intermediate file. Easy enough!</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-puzzle-piece" aria-hidden="true"></i></span>
        <span>Not searching for disconnected categories</span>
      </span>
    </h2>
    <p>We only ever run that search for <i>connected</i> categories. A disconnected category is a disjoint union of
      smaller connected ones, and that decomposition is unique, so a disconnected category is exactly a multiset of
      strictly smaller connected categories — all of which we already have. Rather than searching for them we assemble
      them, which is both far cheaper and, as we'll see below, a check on the arithmetic. In the $(9,3)$ cell this
      means searching for $3{,}568$ categories instead of $60{,}322$.</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-diagram-project" aria-hidden="true"></i></span>
        <span>Counting connected components</span>
      </span>
    </h2>
    <p>Form a graph whose vertices are the objects of a category. Each nonidentity morphism $f\colon x\to y$ supplies an
      undirected edge between $x$ and $y$. We call the category connected when this graph is connected. Every finite
      category is uniquely a disjoint union of connected categories.</p>
    <p>Let $C(n,k)$ be the number of connected categories with $n$ morphisms and $k$ objects. Since a general category
      is a finite multiset of connected categories, we have the formal power-series identity</p>
    <div class="methodology-formula">
      $$
      \sum_{n\geq 0}\sum_{k=0}^{n}T(n,k)x^ny^k
      =
      \prod_{n\geq 1}\prod_{k=1}^{n}
      \left(1-x^ny^k\right)^{-C(n,k)}.
      $$
    </div>
    <p>Here is why. One fixed connected category with $n$ morphisms and $k$ objects may appear zero times, once, twice,
      and so on. Its possible contributions are</p>
    $$
    1+x^ny^k+x^{2n}y^{2k}+\cdots
    =\frac{1}{1-x^ny^k}.
    $$
    <p>There are $C(n,k)$ connected isomorphism classes of that size, which gives the exponent $C(n,k)$. Multiplying
      over all sizes constructs every finite multiset of connected categories, and hence every finite category.</p>
    <p>Consequently, the connected counts in smaller cells determine the number $D(n,k)$ of disconnected categories
      before we inspect the $(n,k)$ file:</p>
    $$
    D(n,k)=
    [x^ny^k]
    \prod_{a=1}^{n-1}\prod_{b=1}^{a}
    \left(1-x^ay^b\right)^{-C(a,b)},
    \qquad
    T(n,k)=C(n,k)+D(n,k).
    $$
    <p>We check more than the final coefficient. If a component size $(a,b)$ occurs $r$ times and there are $c=C(a,b)$
      connected categories of that size, then there are $\binom{c+r-1}{r}$ ways to choose those components. Multiplying
      over the distinct component sizes gives the exact number of categories with that component pattern. Every
      disconnected component pattern in every populated cell occurs with the expected multiplicity.</p>
    <p>This calculation determines the disconnected part of a cell, but it cannot detect a missing connected category:
      that still requires the exhaustive search or a separate argument. This distinction is exactly what we need for the
      two disputed cells.</p>

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-scale-balanced" aria-hidden="true"></i></span>
        <span>Where our counts differ from Cruttwell-Leblanc</span>
      </span>
    </h2>
    <div class="table-container">
      <table class="table is-bordered is-striped methodology-table">
        <thead>
          <tr>
            <th>Morphisms</th>
            <th>Objects</th>
            <th>Cruttwell-Leblanc</th>
            <th>SmallCats</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>9</td>
            <td>3</td>
            <td>60,201</td>
            <td><strong>60,322</strong></td>
          </tr>
          <tr>
            <td>10</td>
            <td>4</td>
            <td>65,922</td>
            <td><strong>68,990</strong></td>
          </tr>
        </tbody>
      </table>
    </div>

    <h3>9 morphisms and 3 objects</h3>
    <p>Start with the part that does not depend on the search in this cell. The smaller connected counts give</p>
    $$
    D(9,3)=56{,}754.
    $$
    <p>The original search finds $C(9,3)=3{,}568$ connected categories. Therefore</p>
    $$
    T(9,3)=56{,}754+3{,}568=60{,}322.
    $$
    <p>The Cruttwell–Leblanc total would instead imply</p>
    $$
    C(9,3)=60{,}201-56{,}754=3{,}447,
    $$
    <p>so their total has a connected contribution $121$ smaller than ours. Their <a
        href="https://www.reluctantm.com/gcruttw/publications/ams2014CruttwellCountingFiniteCats.pdf">presentation</a>
      says that this is where their program changed to counting connected categories and reconstructing the disconnected
      ones, making that stage the natural place to investigate. Without their code or intermediate output, however, we
      cannot distinguish an error in the connected search from an error in the reconstruction or reporting.</p>
    <p>It's worth saying clearly that this is a local disagreement and not a general one. We have since enumerated the
      $(9,2)$ cell as well, and we get $T(9,2)=1{,}868{,}157$ — exactly their number, and the only value in that row
      nobody had checked. Their $T(9,4)$, $T(9,5)$ and $T(9,6)$ agree with us too, as do $T(10,5)$ and $T(10,6)$. The
      two cells we disagree about, $(9,3)$ and $(10,4)$, are precisely the two largest connected searches their method
      had to perform, which is what you would expect from a search that stopped early rather than from a mistake in the
      reasoning.</p>

    <h3>10 morphisms and 4 objects</h3>
    <p>Here the reported value $65{,}922$ cannot satisfy the component formula. Even if we use Cruttwell and Leblanc’s
      lower value at $(9,3)$, their smaller counts produce</p>
    $$
    D_{\mathrm{CL}}(10,4)=67{,}392&gt;65{,}922.
    $$
    <p>There would already be more disconnected categories than their proposed total. Using the corrected value
      $T(9,3)=60{,}322$ instead gives</p>
    $$
    D(10,4)=67{,}513.
    $$
    <p>It remains to count the connected categories. An exhaustive search gives $1{,}465$ connected <em>skeletal</em>
      categories, meaning that no two distinct objects are isomorphic.</p>
    <p>Every nonskeletal category is obtained from its skeleton $S$ by replacing a skeleton object $x$ with $m_x$
      isomorphic copies. The resulting category $\mathcal C$ has</p>
    $$
    \sum_x m_x=4,
    \qquad
    |\operatorname{Mor}(\mathcal C)|
    =\sum_{x,y\in\operatorname{Ob}(S)}
    m_xm_y\,|\operatorname{Hom}_S(x,y)|.
    $$
    <p>If the skeleton has one object, then that object is copied four times, and its identity morphism alone produces
      $4^2=16$ morphisms. If the skeleton has two objects, their multiplicities are either $(3,1)$ or $(2,2)$. The
      identity morphisms contribute $3^2+1^2=10$ or $2^2+2^2=8$ morphisms, and connectedness requires at least one
      morphism between the two objects, contributing another $3$ or $4$. Thus the two-object cases have at least $13$ or
      $12$ morphisms.</p>
    <p>A nonskeletal category in the $(10,4)$ cell must therefore have a three-object skeleton with multiplicities
      $(2,1,1)$. Such a connected skeleton has at least five morphisms. Duplicating one object adds three
      identity-derived morphisms and at least one copy of an incident morphism, so it adds at least four altogether. A
      skeleton which expands to exactly ten morphisms can consequently have only five or six morphisms.</p>
    <p>We now filter the complete $(5,3)$ and $(6,3)$ lists to their connected skeletal entries, mark each possible
      object to duplicate, and quotient those marked choices by automorphisms of the skeleton. Exactly $12$ isomorphism
      classes remain.</p>
    <p>Hence</p>
    $$
    C(10,4)=1{,}465+12=1{,}477
    $$
    <p>and finally</p>
    $$
    \begin{aligned}
    T(10,4)
    &amp;=D(10,4)+C(10,4)\\
    &amp;=67{,}513+1{,}477\\
    &amp;=68{,}990.
    \end{aligned}
    $$

    <h2>
      <span class="icon-text">
        <span class="icon"><i class="fa-solid fa-clipboard-check" aria-hidden="true"></i></span>
        <span>Checking the answer</span>
      </span>
    </h2>
    <p>A search that promises not to repeat itself is only worth as much as the check on that promise, so here are the
      checks we actually run.</p>
    <p>The first is the direct one. For each stored cell we take every table, compute the least relabelling of it under
      the full group $G_{n,k}$ — the canonical form we deliberately avoid computing during the search — and look for
      two tables that produce the same one. Finding none means no two lines of the file describe the same category. We
      also confirm that every line really is a category: identities behave, composites are defined exactly when the
      domains and codomains agree, and every associativity triple holds.</p>
    <p>The second is the component formula above, which predicts the disconnected part of a cell before we build any of
      it. It's a sharper test than the totals suggest, because it also predicts how many categories have each pattern
      of component sizes. Every disconnected category in every populated cell turns up with exactly the multiplicity
      it should.</p>
    <p>The third applies only to the monoids, and comes from outside this project entirely. A finite monoid whose only
      unit is the identity is exactly a semigroup with an identity adjoined, and that correspondence is a bijection, so
      the number of monoids of order $n$ minus the number of semigroups of order $n-1$ has to be the number of monoids
      of order $n$ with a nontrivial group of units. The semigroups of order seven have been counted independently of
      anything here — there are $1{,}627{,}672$ of them (<a href="https://oeis.org/A027851">A027851</a>) — and we find
      $1{,}668{,}997$ monoids of order eight, of which exactly $41{,}325$ have a nontrivial unit group. The difference
      comes out right, and nothing about our search went into the semigroup count.</p>

    <div class="buttons methodology-links">
      <a class="button is-link is-light is-outlined" href="https://github.com/diracdeltafunk/SmallCategories">
        <span class="icon-text">
          <span class="icon"><i class="fa-brands fa-github" aria-hidden="true"></i></span>
          <span>Generator and database</span>
        </span>
      </a>
      <a class="button is-info is-light is-outlined" href="https://oeis.org/A125697">
        <span class="icon-text">
          <span class="icon"><i class="fa-solid fa-table-cells" aria-hidden="true"></i></span>
          <span>OEIS category table</span>
        </span>
      </a>
      <a class="button is-success is-light is-outlined" href="/stats" data-link>
        <span class="icon-text">
          <span class="icon"><i class="fa-solid fa-chart-simple" aria-hidden="true"></i></span>
          <span>Back to statistics</span>
        </span>
      </a>
    </div>
  </div>
</section>